import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import userAPI from "../api/userAPI";
import { getSimulatedPasses, getSimulatedEligibility } from "../testSimulation";

export const fetchMultipleEntryPasses = createAsyncThunk(
  "multipleEntryPass/fetchAll",
  async (params, { rejectWithValue }) => {
    // TEMP-TEST (remove after testing) — see src/testSimulation.js.
    const simulatedPasses = getSimulatedPasses();
    if (simulatedPasses) return simulatedPasses;
    try {
      return await userAPI.getAllMultipleEntryPass(params);
    } catch (err) {
      return rejectWithValue(err.mess);
    }
  },
  {
    // One fetch per session, enforced here rather than at the four call sites: those
    // guards are easy to omit (screens/mainPage.jsx did, so the welcome prefetch and the
    // home page each asked for the same passes), and a new caller would have to
    // rediscover the rule. passesLoading covers what passesFetched can't — it only flips
    // on fulfilled, so two callers racing before the first response both saw false.
    // Pass { force: true } for a deliberate refresh, e.g. after cancelling an order.
    condition: (arg, { getState }) => {
      if (arg?.force) return true;
      const { passesFetched, passesLoading } = getState().multipleEntryPassSlice;
      return !passesFetched && !passesLoading;
    },
  },
);

export const checkCanPurchasePass = createAsyncThunk(
  "multipleEntryPass/checkCanPurchase",
  async (params, { rejectWithValue }) => {
    // TEMP-TEST (remove after testing) — see src/testSimulation.js.
    const simulatedEligibility = getSimulatedEligibility();
    if (simulatedEligibility) return simulatedEligibility;
    try {
      const result = await userAPI.canPassPurchasePossible(params);
      return result;
    } catch (err) {
      return rejectWithValue(err.mess);
    }
  },
  {
    // In-flight dedupe only — unlike the passes above, an already-landed answer
    // does NOT block a re-check: summary and orderHistory legitimately refresh
    // eligibility after a purchase or a cancellation. This just collapses the
    // near-simultaneous pair fired on home entry (useHomeData's early dispatch
    // and mainPage's effect) into one request.
    condition: (arg, { getState }) => arg?.force || !getState().multipleEntryPassSlice.canPurchaseLoading,
  },
);

export const multipleEntryPassSlice = createSlice({
  name: "multipleEntryPassSlice",
  initialState: {
    passes: [],
    passesFetched: false,
    // In-flight marker for the thunk's condition — passesFetched alone can't dedupe
    // concurrent callers, since it only flips once a response lands.
    passesLoading: false,
    // True after a fetch round *settled* with an error. passesFetched only flips
    // on fulfilled, so without this a failed round would look identical to
    // "never asked" and wedge the home loader gate open-ended.
    passesError: false,
    canPurchaseNewPass: null,
    // In-flight / settled markers for checkCanPurchasePass, mirroring the passes
    // pair above: canPurchaseLoading feeds the thunk's dedupe condition, and
    // canPurchaseChecked flips on fulfilled AND rejected so the home loader gate
    // treats a failed check as settled rather than waiting forever.
    canPurchaseLoading: false,
    canPurchaseChecked: false,
    purchasePassReasonFlag: null,
    isRegularMaofEntryPossible: null,
    isMaofPurchasePass: null,
    isMaofPassNotExpire: null,
  },
  reducers: {
    addNewMultipleEntryPass: (state, action) => {
      if (!state.passes) {
        state.passes = [];
      }
      state.passes.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMultipleEntryPasses.pending, (state) => {
        state.passesLoading = true;
        state.passesError = false;
      })
      .addCase(fetchMultipleEntryPasses.fulfilled, (state, action) => {
        state.passes = action.payload;
        state.passesFetched = true;
        state.passesLoading = false;
      })
      .addCase(fetchMultipleEntryPasses.rejected, (state, action) => {
        // Cleared but passesFetched left false, so a failure can be retried rather
        // than locking the passes out for the session.
        state.passesLoading = false;
        state.passesError = true;
        console.error("Failed to fetch multiple entry passes:", action.payload);
      })
      .addCase(checkCanPurchasePass.pending, (state) => {
        state.canPurchaseLoading = true;
      })
      .addCase(checkCanPurchasePass.fulfilled, (state, action) => {
        state.canPurchaseLoading = false;
        state.canPurchaseChecked = true;
        state.canPurchaseNewPass = action.payload.IsPassPurchasePossible;
        state.purchasePassReasonFlag = action.payload.IsRegularOrderFound;
        state.isRegularMaofEntryPossible = action.payload.IsRegularMaofEntryPossible ?? null;
        state.isMaofPurchasePass = action.payload.IsMaofPurchasepass ?? null;
        state.isMaofPassNotExpire = action.payload.IsMaofpassNotExpire ?? null;
      })
      .addCase(checkCanPurchasePass.rejected, (state, action) => {
        state.canPurchaseLoading = false;
        state.canPurchaseChecked = true;
        console.error("Failed to check can purchase pass:", action.payload);
      });
  },
});

export const { addNewMultipleEntryPass } = multipleEntryPassSlice.actions;

export const selectMultipleEntryPasses = (state) => state.multipleEntryPassSlice.passes;
export const selectPassesFetched = (state) => state.multipleEntryPassSlice.passesFetched;
// Settled = answered, success or failure — what the home loader gate waits on.
export const selectPassesSettled = (state) =>
  state.multipleEntryPassSlice.passesFetched || state.multipleEntryPassSlice.passesError;
export const selectCanPurchaseNewPass = (state) => state.multipleEntryPassSlice.canPurchaseNewPass;
export const selectCanPurchaseLoading = (state) => state.multipleEntryPassSlice.canPurchaseLoading;
export const selectCanPurchaseChecked = (state) => state.multipleEntryPassSlice.canPurchaseChecked;
export const selectPurchasePassReasonFlag = (state) => state.multipleEntryPassSlice.purchasePassReasonFlag;
export const selectIsRegularMaofEntryPossible = (state) => state.multipleEntryPassSlice.isRegularMaofEntryPossible;
export const selectIsMaofPurchasePass = (state) => state.multipleEntryPassSlice.isMaofPurchasePass;
export const selectIsMaofPassNotExpire = (state) => state.multipleEntryPassSlice.isMaofPassNotExpire;

// Returns true if any pass in the array was purchased in the current calendar month.
// Verify the date field name (PurchaseDate) against the actual API response.
export function hasMaofPassThisMonth(passes) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  return (passes || []).some((pass) => {
    const raw = pass.PurchaseDate;
    if (!raw) return false;
    const datePart = raw.split(" ")[0];
    const parts = datePart.split(".");
    if (parts.length < 3) return false;
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return month === currentMonth && year === currentYear;
  });
}

export const activeMultipleEntryPassesSelector = createSelector([selectMultipleEntryPasses], (passes) =>
  (passes || []).filter((pass) => pass.IsAvailable),
);

export const activeMultipleEntryPassesDetailsSelector = createSelector([selectMultipleEntryPasses], (passes) =>
  (passes || []).find((pass) => pass.IsAvailable),
);

export const inactiveMultipleEntryPassesSelector = createSelector([selectMultipleEntryPasses], (passes) =>
  (passes || []).filter((pass) => !pass.IsAvailable),
);

export const hasActiveMultipleEntryPassSelector = createSelector([selectMultipleEntryPasses], (passes) =>
  (passes || []).some((pass) => pass.IsAvailable),
);

export const canBuyNewMultipleEntryPassSelector = selectCanPurchaseNewPass;

export const purchasePassReasonFlagSelector = selectPurchasePassReasonFlag;

export default multipleEntryPassSlice.reducer;
